import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import Pagination from '@/components/Pagination';
import { useToast } from '@/components/ui/use-toast';
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { useAdminStore } from '../../store/useAdmin.store';

export interface RowData {
  surname: string;
  forename: string;
  email: string;
}

const ExcelUploader = ({ onImportStaff }: { onImportStaff: (users?: RowData[]) => Promise<void> }) => {
  const [data, setData] = useState<RowData[]>([
    {
      surname: 'John',
      forename: 'Doe',
      email: 'john.doe@email.com',
    },
  ]);
  const fileInput = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [spreadsheetUploaded, setSpreadsheetUploaded] = useState<boolean>(false);
  const { toast } = useToast();
  const { page } = useAdminStore();

  const rowsPerPage = 10;

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    const file = event.target.files[0];

    // Read the Excel file
    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target) return;
      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });

      // Assuming data is in the first sheet
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      const jsonData = XLSX.utils.sheet_to_json<{
        surname: string;
        Surname: string;
        forename: string;
        Forename: string;
        email: string;
        Email: string;
      }>(worksheet);

      // Extract required columns
      const filteredData = jsonData.map((row) => ({
        surname: row.surname || row.Surname,
        forename: row.forename || row.Forename,
        email: row.email || row.Email,
      }));

      if (filteredData.some((row) => !row.email)) {
        toast({
          description: 'Some rows are missing email address',
          variant: 'destructive',
        });
        if (fileInput.current) {
          fileInput.current.value = '';
        }
        return;
      }

      setData(filteredData);
      if (fileInput.current) {
        fileInput.current.value = '';
      }
      setSpreadsheetUploaded(true);
    };
    reader.readAsBinaryString(file);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const importStaff = async () => {
    setIsLoading(true);
    await onImportStaff(data);
    setOpen(false);
    setIsLoading(false);
  };
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const paginatedData = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <div className="flex gap-4 pl-4">
      <Button theme="solid" onClick={() => setOpen(true)}>
        {page?.schoolManagement?.invitationUsingFile?.button}
      </Button>

      <input type="file" accept=".xlsx,.xls" ref={fileInput} onChange={handleFileUpload} className="mb-4 hidden" />

      <Dialog
        open={open}
        setOpen={setOpen}
        title={page?.schoolManagement.invitationUsingFile?.dialogTitle || 'Preview'}
        body={
          <>
            <p className=" mb-4 text-sh-xs font-bold ">
              {page?.schoolManagement.invitationUsingFile?.warningMsgDialog}
            </p>
            <table className="w-full table-auto">
              <thead>
                <tr>
                  <th className="px-4 py-2">Surname</th>
                  <th className="px-4 py-2">Forename</th>
                  <th className="px-4 py-2">Email</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, index) => (
                  <tr key={index}>
                    <td className="border px-4 py-2">{row.surname}</td>
                    <td className="border px-4 py-2">{row.forename}</td>
                    <td className="border px-4 py-2">{row.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              currentPage={currentPage}
              pageSize={totalPages}
              onChangePage={(page: number) => {
                handlePageChange(page);
              }}
            />
            <div className="flex justify-end gap-4">
              <Button theme="solid" className="mt-5" onClick={() => fileInput.current?.click()} loading={isLoading}>
                {page?.schoolManagement.invitationUsingFile?.uploadFileBtn}
              </Button>
              <Button
                theme="solid"
                className="mt-5"
                onClick={() => importStaff()}
                loading={isLoading}
                disabled={!spreadsheetUploaded}>
                {page?.schoolManagement.invitationUsingFile?.sendInvitationBtn}
              </Button>
              <Button theme="outline" className="mt-5" onClick={() => setOpen(false)}>
                {page?.schoolManagement.invitationUsingFile?.closeBtn}
              </Button>
            </div>
          </>
        }
      />
    </div>
  );
};

export default ExcelUploader;
